import React, { useMemo } from 'react';
import { Target, ArrowRight, Clock } from 'lucide-react';
import { useAppStore } from '../app/hooks/useAppStore';
import { getWeakestFamiliesGlobal, countFamiliesByMasteryLevel, getFamilyMasteryKey, getEvidenceLevelForFamily, getOverallEvidenceLevel, MIN_ANSWERS_FOR_CLAIM } from '../mastery';
import { getFamilyDefinition, MODULES, QUESTIONS_BY_FAMILY, getAllowedTiersForGroup } from '../content';
import { getLDG, calculateFrontier } from '../governance';
import { buildFrontierMasteryInputs, MODE_REGISTRY } from '../modes';
import type { ModeId as RegistryModeId } from '../modes';
import type { GroupId } from '../config/systemConfig';
import { CardSurface } from './shared/CardSurface';
import { MODE_ICON } from './shared/modeTheme';
import { getMasteryLabelInfo } from './shared/masteryLabelInfo';
import { LabStartPanel } from './shared/LabStartPanel';
import { useLabEvidence } from './hooks/useLabEvidence';
// ─── Types ────────────────────────────────────────────────────
interface MissionBriefingProps {
  onModeClick: (modeId: string, familyId?: string) => void;
}
/**
 * Waar een voorstel op gebaseerd is.
 *.
 * Dit veld bestond niet: `buildRecommendations` kende zijn eigen herkomst wel
 * (frontier, zwakste, fluency-ready, fallback) maar gooide die weg, waarna de
 * UI op de EERSTE kaart onvoorwaardelijk "Beste keuze voor jou" plakte. Daardoor
 * kon een generiek noodgreep-voorstel zich voordoen als persoonlijk advies.
 *
 * De badge volgt nu uit de herkomst in plaats van uit de positie in de lijst.
 */
type RecommendationBasis = /** Beginpunt uit de leerlijn. Zegt iets over de LEERSTOF, niet over het kind. */
'curriculum'
/** Zwakste onderwerp, met genoeg antwoorden om dat te mogen zeggen. */ | 'weakest'
/** Kent het al, klaar voor snelheid. */ | 'fluency_ready'
/** Beheerst het, klaar voor een hoger niveau. */ | 'challenge_ready'
/** Geen grond. Mag géén badge krijgen. */ | 'generic';
interface Recommendation {
  id: string;
  familyId?: string;
  title: string;
  reason: string;
  basis: RecommendationBasis;
  /** Modulenaam, alleen gevuld bij leerlijn-voorstellen (voor het startpaneel). */
  moduleLabel?: string;
  color: 'amber' | 'indigo' | 'violet' | 'teal';
}

/**
 * Het label boven de kaart, afgeleid uit de herkomst.
 *
 * `generic` levert bewust `null` op: dan verdwijnt de badge, in plaats van dat
 * er een andere tekst voor in de plaats komt. Een voorstel zonder grond hoort
 * geen kopje te krijgen dat suggereert dat er wel een grond was.
 */
function getBasisLabel(basis: RecommendationBasis): string | null {
  switch (basis) {
    case 'curriculum':
      return 'Goed om mee te beginnen';
    case 'weakest':
      return 'Hier kun je nog groeien';
    case 'fluency_ready':
      return 'Dit ken je al';
    case 'challenge_ready':
      return 'Klaar voor de uitdaging';
    case 'generic':
      return null;
  }
}
// ─── Color Palette ────────────────────────────────────────────
const COLOR_CLASSES = {
  amber: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    border: 'border-amber-300',
    gradient: 'from-amber-400 to-orange-500',
    shadow: 'shadow-amber-200'
  },
  indigo: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-700',
    border: 'border-indigo-300',
    gradient: 'from-indigo-400 to-purple-500',
    shadow: 'shadow-indigo-200'
  },
  violet: {
    bg: 'bg-violet-100',
    text: 'text-violet-700',
    border: 'border-violet-300',
    gradient: 'from-violet-400 to-fuchsia-500',
    shadow: 'shadow-violet-200'
  },
  teal: {
    bg: 'bg-teal-100',
    text: 'text-teal-700',
    border: 'border-teal-300',
    gradient: 'from-teal-400 to-emerald-500',
    shadow: 'shadow-teal-200'
  }
} as const;
// ─── Helpers ──────────────────────────────────────────────────
/**
 * Check if a family has actual questions available for a given group.
 * Prevents recommending families that exist in taxonomy but have
 * no questions within the allowed tier range for the student's group.
 */
function hasFamilyQuestionsForGroup(familyId: string, group: GroupId): boolean {
  const familyQuestions = QUESTIONS_BY_FAMILY.get(familyId);
  if (!familyQuestions || familyQuestions.length === 0) return false;
  const allowedTiers = getAllowedTiersForGroup(group);
  return familyQuestions.some(q => allowedTiers.has(q.tier));
}
// ─── Recommendation Engine ────────────────────────────────────
function buildRecommendations(familyMastery: Record<string, {
  masteryScore: number;
  attempts: number;
}>, selectedGroup: GroupId): Recommendation[] {
  const recs: Recommendation[] = [];
  // 1. LDG Frontier (Volgende stap)
  //
  // Bij nul mastery geeft de frontier alle root-nodes terug: families zonder
  // harde voorkennis, die dus per definitie "klaar" zijn. Dat is inhoudelijk
  // een verdedigbaar beginpunt — maar het is gekozen door de LEERLIJN, niet
  // door iets over dit kind. Daarom `basis: 'curriculum'` en niet 'weakest':
  // het label mag geen persoonlijke kennis suggereren die er niet is.
  const masteryInputs = buildFrontierMasteryInputs(familyMastery, selectedGroup);
  const frontierResult = calculateFrontier(getLDG(), masteryInputs, {
    maxFrontierSize: 5,
    masteryThreshold: 80
  });
  if (frontierResult.frontier.length > 0) {
    const playableFrontier = frontierResult.frontier.filter(f => hasFamilyQuestionsForGroup(f.familyId, selectedGroup));
    playableFrontier.slice(0, 2).forEach((f, idx) => {
      recs.push({
        id: 'sprint',
        familyId: f.familyId,
        title: f.skill,
        reason: `Volgende stap: ${f.module}`,
        basis: 'curriculum',
        moduleLabel: f.module,
        color: idx === 0 ? 'teal' : 'indigo'
      });
    });
  }
  if (recs.length >= 2) return recs.slice(0, 2);
  // 2. Weakest families → Focus Sprint
  //
  // De selector filtert alleen op `attempts === 0`, dus ÉÉN antwoord was al
  // genoeg om een onderwerp "je zwakste" te noemen. Dat is een gok met de
  // stelligheid van een claim. Hier geldt daarom de ondergrens: onder
  // MIN_ANSWERS_FOR_CLAIM antwoorden komt dit voorstel er niet, en valt de
  // keten door naar een reden die wél klopt.
  const weakest = getWeakestFamiliesGlobal(familyMastery, selectedGroup, 5).filter(w => hasFamilyQuestionsForGroup(w.familyId, selectedGroup)).filter(w => getEvidenceLevelForFamily(familyMastery, w.familyId, selectedGroup) === 'sufficient');
  if (weakest.length > 0 && !recs.some(r => r.familyId === weakest[0].familyId)) {
    const def = getFamilyDefinition(weakest[0].familyId);
    // Geen percentage in de reden. Een kind kan met "62%" niets, het nodigt uit
    // tot vergelijken, en het is het laatste percentage dat hier stond terwijl
    // de rest van het domein het beleid uit masteryLabelInfo.ts al volgt.
    recs.push({
      id: 'sprint',
      familyId: weakest[0].familyId,
      title: 'Focus Sprint',
      reason: def ? `Focus op ${def.description}` : 'Train het onderwerp waar je nog het meest kunt groeien',
      basis: 'weakest',
      color: 'indigo'
    });
  }
  if (recs.length >= 2) return recs.slice(0, 2);
  // 3. Fluency-ready families
  //
  // "Je kent het al" is een uitspraak over het kind, dus alleen boven de
  // ondergrens. Zonder die controle stond die tekst er ook bij één antwoord.
  const hasEvidence = getOverallEvidenceLevel(familyMastery, selectedGroup) === 'sufficient';
  let hasFluencyFamilies = false;
  for (const mod of MODULES) {
    const counts = countFamiliesByMasteryLevel(familyMastery, mod.id, selectedGroup);
    if (counts.oefenaar > 0 || counts.gevorderd > 0) {
      hasFluencyFamilies = true;
      break;
    }
  }
  if (hasFluencyFamilies && hasEvidence) {
    recs.push({
      id: 'fluency',
      title: 'Vloeiend Oefenen',
      reason: 'Je kent het al — oefen nu op snelheid zodat het automatisch gaat',
      basis: 'fluency_ready',
      color: 'amber'
    });
  }
  if (recs.length >= 2) return recs.slice(0, 2);
  // 4. Challenge-ready — óók een uitspraak over het kind ("je beheerst dit").
  const hasChallengeFamilies = Object.values(familyMastery).some(m => m.masteryScore >= 70 && m.attempts >= MIN_ANSWERS_FOR_CLAIM);
  if (hasChallengeFamilies) {
    recs.push({
      id: 'challenge',
      title: 'Uitdaging',
      reason: 'Je beheerst dit goed genoeg — probeer het nu op een hoger niveau',
      basis: 'challenge_ready',
      color: 'violet'
    });
  }
  // Fallback — vult aan tot twee kaarten wanneer er niets te onderbouwen was.
  //
  // Deze tak blijft bestaan: een leeg lab is voor een kind geen bruikbaar
  // antwoord. Maar de voorstellen dragen nu `basis: 'generic'`, en daardoor
  // krijgen ze GEEN badge. Dat was de kern van het probleem — deze generieke
  // teksten stonden onder het kopje "Beste keuze voor jou".
  const GENERIC_FLUENCY: Recommendation = {
    id: 'fluency',
    title: 'Vloeiend Oefenen',
    reason: 'Oefen dagelijks op snelheid — zo wordt het automatisch',
    basis: 'generic',
    color: 'amber'
  };
  const GENERIC_SPRINT: Recommendation = {
    id: 'sprint',
    title: 'Focus Sprint',
    reason: 'Kies een onderwerp en train het grondig met uitleg',
    basis: 'generic',
    color: 'indigo'
  };
  if (recs.length === 0) {
    recs.push(GENERIC_FLUENCY, GENERIC_SPRINT);
  }
  if (recs.length === 1) {
    recs.push(recs[0].id !== 'fluency' ? GENERIC_FLUENCY : GENERIC_SPRINT);
  }
  return recs.slice(0, 2);
}
// ─── Component ────────────────────────────────────────────────
/**
 * MissionBriefing — Djay's personalized training recommendations.
 *
 * Uses the LDG frontier, weakest families, and mastery levels to suggest
 * the two most relevant practice modes for the student.
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function MissionBriefing({
  onModeClick
}: MissionBriefingProps) {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const familyMastery = state.familyMastery || {};
  const {
    isEmpty
  } = useLabEvidence();
  const recommendations = useMemo(() => buildRecommendations(familyMastery, selectedGroup), [familyMastery, selectedGroup]);

  // Zolang we niets weten, vervangt het startpaneel de twee kaarten.
  //
  // Bewust HIER en niet in LabHero: dit onderdeel bezit de aanbevelingsketen,
  // dus het beginpunt uit de leerlijn is hier al berekend. Het elders opnieuw
  // uitrekenen zou een tweede bron opleveren die van de eerste kan afwijken —
  // precies het patroon dat we in dit domein aan het opruimen zijn.
  if (isEmpty) {
    const curriculumStart = recommendations.find(r => r.basis === 'curriculum' && r.familyId);
    return <LabStartPanel startSuggestion={curriculumStart?.familyId ? {
      familyId: curriculumStart.familyId,
      title: curriculumStart.title,
      module: curriculumStart.moduleLabel ?? ''
    } : undefined} onStartSuggestion={familyId => onModeClick('sprint', familyId)} onFreePractice={() => onModeClick('free')} data-id="element-3077" data-mp="11t39h4ap810g" />;
  }
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3078" data-mp="j1alxpeef804">
      {recommendations.map((rec, index) => {
      const colors = COLOR_CLASSES[rec.color];
      const RecIcon = MODE_ICON[rec.id as RegistryModeId] || Target;
      const registryEntry = MODE_REGISTRY[rec.id as RegistryModeId];
      const rawEstimate = registryEntry?.estimatedMinutes;
      const estimateMin = typeof rawEstimate === 'number' ? rawEstimate : undefined;
      const masteryScore = rec.familyId ? familyMastery[getFamilyMasteryKey(rec.familyId, selectedGroup)]?.masteryScore : undefined;
      const isPrimary = index === 0;
      const basisLabel = getBasisLabel(rec.basis);
      return <CardSurface key={`${rec.id}-${rec.familyId || index}`} onClick={() => onModeClick(rec.id, rec.familyId)} className={`rounded-2xl p-4 sm:p-5 bg-gradient-to-br ${colors.gradient} text-white shadow-md ${colors.shadow} overflow-hidden group flex flex-col h-full ${isPrimary ? 'md:col-span-2' : 'md:col-span-1'} before:absolute before:top-0 before:right-0 before:w-32 before:h-32 before:bg-white/20 before:rounded-full before:blur-2xl before:-translate-y-1/2 before:translate-x-1/4 before:pointer-events-none`} data-id="element-3079" data-mp="eaf5zqwa9ol0" data-mpi={index}>
            
            <div className={`flex items-start gap-4 relative z-10 flex-1 w-full ${isPrimary ? 'sm:items-center' : ''}`} data-id="element-3080" data-mp="194he978c8pxz">
              
              <div className={`${isPrimary ? 'w-14 h-14 sm:w-16 sm:h-16' : 'w-12 h-12'} bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`} data-id="element-3081" data-mp="1e2ixbs5rkp9g">
                
                <RecIcon className={`${isPrimary ? 'w-7 h-7 sm:w-8 sm:h-8' : 'w-6 h-6'} text-white`} strokeWidth={2.5} data-id="element-3082" data-mp="1rvi8jhyijxb7" />
                
              </div>
              <div className="flex-1 min-w-0" data-id="element-3083" data-mp="1wv4o3y1tdpn6">
                {/* Het label volgt uit de HERKOMST, niet uit de positie. Hier
                     stond "Beste keuze voor jou" op elke eerste kaart — ook op
                     het generieke fallback-voorstel, waar niets over het kind
                     bekend is. Bij `basis: 'generic'` is dit `null` en verdwijnt
                     het kopje. */}
                {basisLabel && <div className="text-[10px] font-black uppercase tracking-wider text-white/80 mb-1" data-id="element-3084" data-mp="3w3mq47h8b0q3">
                    {basisLabel}
                  </div>}
                <h3 className={`${isPrimary ? 'text-xl sm:text-2xl' : 'text-lg'} font-black leading-tight mb-1`} data-id="element-3085" data-mp="3mcs4n5bt455p">
                  
                  {rec.title}
                </h3>
                <p className={`text-white/90 ${isPrimary ? 'text-sm sm:text-base' : 'text-sm'} font-medium leading-snug`} data-id="element-3086" data-mp="ivjn6mkzr7bi">
                  
                  {rec.reason}
                </p>
                {/* F-UI-2: context chips — mastery + time */}
                <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-3087" data-mp="8yx3400hokaj">
                  {masteryScore !== undefined && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/20 backdrop-blur-sm text-[10px] font-black uppercase tracking-wider" data-id="element-3088" data-mp="403pm7l7ctad">
                      {getMasteryLabelInfo(masteryScore).label}
                    </span>}
                  {estimateMin !== undefined && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/20 backdrop-blur-sm text-[10px] font-black uppercase tracking-wider" data-id="element-3089" data-mp="hbglhiee4fnq">
                      <Clock className="w-3 h-3" data-id="element-3090" data-mp="7fn6zth42c3b" />
                      {estimateMin}m
                    </span>}
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-white/20 flex items-center justify-end relative z-10 w-full" data-id="element-3091" data-mp="102pzo4z3vsln">
              <span className="text-xs font-bold text-white/90 mr-2 uppercase tracking-wider group-hover:text-white transition-colors" data-id="element-3092" data-mp="152m1bxkbz7y1">
                Start Missie
              </span>
              <ArrowRight className="w-5 h-5 text-white/80 group-hover:text-white group-hover:translate-x-1 transition-all" data-id="element-3093" data-mp="v3n2t7ta4sor" />
            </div>
          </CardSurface>;
    })}
    </div>;
}